import { toast } from "vue3-toastify";

export function extractStoredFileName(filePath) {
  if (!filePath) return "";
  const withoutQuery = String(filePath).split("?")[0];
  return decodeURIComponent(withoutQuery.split("/").pop() || "");
}

export function extractDisplayFileName(filePath, fallback = "document") {
  const stored = extractStoredFileName(filePath);
  if (!stored) return fallback;
  const idx = stored.lastIndexOf("_");
  return idx >= 0 ? stored.substring(idx + 1) : stored;
}

function normalizeFileUrl(url) {
  return String(url).replace(/^http:\/\//i, "https://");
}

function getFileExtension(name) {
  return name.split(".").pop()?.toLowerCase() || "";
}

function isImageFile(name) {
  return ["jpg", "jpeg", "png", "gif", "webp", "bmp"].includes(
    getFileExtension(name)
  );
}

function getContentType(fileName, response) {
  const fromHeader = response.headers
    .get("content-type")
    ?.split(";")[0]
    ?.trim();
  if (fromHeader && fromHeader !== "application/octet-stream") {
    return fromHeader;
  }

  const mimeTypes = {
    jpg: "image/jpeg",
    jpeg: "image/jpeg",
    png: "image/png",
    gif: "image/gif",
    webp: "image/webp",
    bmp: "image/bmp",
    pdf: "application/pdf",
  };
  return mimeTypes[getFileExtension(fileName)] || "application/octet-stream";
}

function writePreviewPage(previewTab, blobUrl, title, isImage) {
  previewTab.document.open();
  if (isImage) {
    previewTab.document.write(
      `<!DOCTYPE html><html><head><meta charset="utf-8"><title>${title}</title>` +
        `<style>*{margin:0}body{min-height:100vh;display:flex;align-items:center;justify-content:center;background:#111}` +
        `img{max-width:100%;max-height:100vh;object-fit:contain}</style></head>` +
        `<body><img src="${blobUrl}" alt="${title}" /></body></html>`
    );
  } else {
    previewTab.document.write(
      `<!DOCTYPE html><html><head><meta charset="utf-8"><title>${title}</title>` +
        `<style>*{margin:0}html,body,iframe{width:100%;height:100%;border:0}</style></head>` +
        `<body><iframe src="${blobUrl}"></iframe></body></html>`
    );
  }
  previewTab.document.close();
}

export async function previewStoredFile(fileUrl, fileName) {
  if (!fileUrl) return;

  const url = normalizeFileUrl(fileUrl);
  const title = (fileName || extractStoredFileName(url)).replace(/[<>&"]/g, "");
  const previewTab = window.open("about:blank", "_blank");

  if (!previewTab) {
    toast.error("يرجى السماح بفتح النوافذ المنبثقة");
    return;
  }

  previewTab.document.write(
    `<!DOCTYPE html><html><head><meta charset="utf-8"><title>جاري التحميل...</title>` +
      `<style>body{margin:0;min-height:100vh;display:flex;align-items:center;justify-content:center;font-family:sans-serif;color:#555}</style></head>` +
      `<body>جاري تحميل الملف...</body></html>`
  );
  previewTab.document.close();

  try {
    const isImage = isImageFile(fileName) || isImageFile(url);

    try {
      const fileResponse = await fetch(url);
      if (!fileResponse.ok) {
        throw new Error("Failed to load file");
      }

      const mimeType = getContentType(title, fileResponse);
      const blob = new Blob([await fileResponse.arrayBuffer()], {
        type: mimeType,
      });
      const blobUrl = URL.createObjectURL(blob);
      writePreviewPage(
        previewTab,
        blobUrl,
        title,
        isImage || mimeType.startsWith("image/")
      );
    } catch {
      writePreviewPage(previewTab, url, title, isImage);
    }
  } catch (error) {
    previewTab.close();
    console.error("Failed to open file:", error);
    toast.error("فشل في فتح الملف");
  }
}

export async function downloadStoredFile(fileUrl, fileName) {
  if (!fileUrl) return;

  const url = normalizeFileUrl(fileUrl);

  try {
    const fileResponse = await fetch(url);
    if (!fileResponse.ok) {
      throw new Error("Failed to download file");
    }

    const blob = await fileResponse.blob();
    const link = document.createElement("a");
    const blobUrl = URL.createObjectURL(blob);
    link.href = blobUrl;
    link.download = fileName || extractDisplayFileName(url);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(blobUrl);
  } catch (error) {
    console.error("Download failed:", error);
    window.open(url, "_blank", "noopener,noreferrer");
  }
}
